import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import CustomSnackBar from '../audienceManager/customSnackBar';
import DataTable from 'components/DataTable/DataTable';
import GhostLoader from 'react-ghost-loader';
import Header from 'components/Header';
import Link from 'components/Link';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SegmentSelectionToolbar from './components/segmentSelectionToolbar';
import TextFieldSearch from 'components/TextFieldSearch/TextFieldSearch';
import _debounce from 'lodash/debounce';
import moment from 'moment';
import {
  BulkActionConfirmModal,
  BulkDeleteResultsModal,
} from './components/bulkDeleteModal';
import {
  BulkDeleteConfirmList,
  BulkDeleteConfirmListItem,
  ConfirmModalBodyStack,
} from './components/bulkDeleteModal/index.styles';
import { CaretDown } from '@phosphor-icons/react';
import {
  LoadingStates,
  RateLevel,
  Routes,
  SEGMENT_MANAGER_DEFAULT_PLATFORM,
  SEGMENT_MANAGER_DEFAULT_RATE_LEVEL,
  SEGMENT_MANAGER_DEFAULT_SORT_BY,
  SEGMENT_MANAGER_DEFAULT_SORT_ORDER,
} from '../../constants';
import {
  OAUTH_PLATFORMS_FILTER,
  OTS_SGM_MNG_BULK_DELETE_NETWORK_ERROR,
  OTS_SGM_MNG_CANCEL,
  OTS_SGM_MNG_CONFIRM_BULK_DELETE_TITLE,
  OTS_SGM_MNG_CONFIRM_DELETE,
  OTS_SGM_MNG_LAST_SYNCED,
  OTS_SGM_MNG_NO_SEGMENTS_FOUND,
  OTS_SGM_MNG_SEARCH_SEGMENT_NAME,
  SEGMENT_MANAGER,
  SEGMENT_RATE_LEVEL_FILTER,
} from '../../content';
import { PLATFORM_OPTIONS, RATE_LEVEL_OPTIONS } from './constants';
import {
  type SetTtdOtsBuyableSegmentsPayload,
  useSetTtdOtsBuyableSegments,
} from '../../graphql/hooks/useSetTtdOtsBuyableSegments';
import {
  ThemeProvider,
  createStyles,
  createTheme,
  makeStyles,
} from '@material-ui/core/styles';
import {
  addNotification,
  clearSegmentManagerSelection,
  fetchSegments,
  mergeSegmentsOntoManagerSelection,
  removeNotification,
  removeSegmentIdsFromManagerSelection,
  toggleSegmentManagerSelection,
  updateFilters,
  updateSearchAndResetPage,
} from './slice';
import { buildSegmentDetailQueryParams } from './utils/urlHelpers';
import { buildSegmentDetailUrl, getColumnsByPlatform } from './utils/utils';
import { renderRowCellsByPlatform } from './utils/renderers';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import * as Styled from './index.styles';

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: '100%',
    },
  })
);

const inputs = createTheme({
  overrides: {
    MuiFormControl: {
      root: {
        maxHeight: 48,
      },
    },
    MuiInputLabel: {
      root: {
        marginTop: -5,
        '&$focused': {
          marginTop: 0,
        },
      },
    },
    MuiInputBase: {
      root: {
        maxHeight: 48,
      },
      input: {
        borderRadius: 10,
        alignSelf: 'center',
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: 10,
      },
      input: {
        padding: '12px 14px',
      },
    },
  },
});

const SegmentManager: React.FC = () => {
  const classes = useStyles();
  // Initialize hooks first
  const dispatch = useAppDispatch();
  const [searchText, setSearchText] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState(
    SEGMENT_MANAGER_DEFAULT_PLATFORM
  );
  const [rateLevel, setRateLevel] = useState<RateLevel>(
    SEGMENT_MANAGER_DEFAULT_RATE_LEVEL
  );
  const [sortBy, setSortBy] = useState<string>(SEGMENT_MANAGER_DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>(
    SEGMENT_MANAGER_DEFAULT_SORT_ORDER
  );
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmDeletePending, setConfirmDeletePending] = useState(false);
  const [resultsModalOpen, setResultsModalOpen] = useState(false);
  const [deleteResultsPayload, setDeleteResultsPayload] =
    useState<SetTtdOtsBuyableSegmentsPayload | null>(null);
  const [deleteResultsFailedRows, setDeleteResultsFailedRows] = useState<
    { segmentId: string; segmentName: string; detailUrl: string }[]
  >([]);

  const [setTtdOtsBuyableSegments, { loading: deleteMutationLoading }] =
    useSetTtdOtsBuyableSegments();

  // Get state after hooks are initialized
  const segmentManagerState = useAppSelector(
    (state) => state.segmentManagerReducer
  );

  // Get debouncedSearchText from Redux
  const debouncedSearchText =
    segmentManagerState?.ui?.filters?.debouncedSearchText || '';

  // Defensive check for state
  const entities = segmentManagerState?.entities || {
    segments: [],
    lastSyncAt: undefined,
    totalRecords: 0,
  };
  const ui = segmentManagerState?.ui || {
    loading: { state: LoadingStates.IDLE },
    filters: { page: 1, recordPerPage: 25 },
    notifications: [] as { state: string; message: string }[],
    segmentSelectionById: {},
  };
  const notifications = ui?.notifications || [];
  const segmentSelectionById = ui?.segmentSelectionById || {};
  const selectedSegmentIds = useMemo(
    () => Object.keys(segmentSelectionById),
    [segmentSelectionById]
  );
  const selectedSegmentCount = selectedSegmentIds.length;

  const { bulkDeleteConfirmHeaderSummary, bulkDeleteConfirmBody } =
    useMemo(() => {
      const platformLower = (
        selectedPlatform || SEGMENT_MANAGER_DEFAULT_PLATFORM
      ).toLowerCase();
      const rows = selectedSegmentIds
        .map((segmentId) => {
          const meta = segmentSelectionById[segmentId];
          if (!meta) return null;
          const queryString = buildSegmentDetailQueryParams(
            meta.rateLevel || RateLevel.SYSTEM,
            meta.advertiserId,
            meta.partnerId
          );
          const to = `${Routes.SEGMENT_MANAGER}/${platformLower}/${segmentId}${
            queryString ? `?${queryString}` : ''
          }`;
          return {
            segmentId,
            name: meta.segmentName || segmentId,
            to,
          };
        })
        .filter((row): row is { segmentId: string; name: string; to: string } =>
          Boolean(row)
        );

      rows.sort((a, b) => {
        const byName = a.name.localeCompare(b.name, undefined, {
          sensitivity: 'base',
        });
        return byName !== 0 ? byName : a.segmentId.localeCompare(b.segmentId);
      });

      const headerSummary = (
        <>
          Delete {rows.length} {rows.length === 1 ? 'segment' : 'segments'}?
        </>
      );

      const body = (
        <ConfirmModalBodyStack>
          <BulkDeleteConfirmList>
            {rows.map(({ segmentId, name, to }) => (
              <BulkDeleteConfirmListItem key={segmentId}>
                <Link to={to} target="_blank" rel="noopener noreferrer">
                  {name}
                </Link>
              </BulkDeleteConfirmListItem>
            ))}
          </BulkDeleteConfirmList>
        </ConfirmModalBodyStack>
      );

      return {
        bulkDeleteConfirmHeaderSummary: headerSummary,
        bulkDeleteConfirmBody: body,
      };
    }, [selectedSegmentIds, segmentSelectionById, selectedPlatform]);

  const loading = ui?.loading?.state === LoadingStates.LOADING;
  const error = ui?.loading?.state === LoadingStates.ERROR;

  // Format lastSyncAt for display
  const formattedDate = entities.lastSyncAt
    ? moment(entities.lastSyncAt).format('MM/DD/YYYY')
    : '';

  const formattedTime = entities.lastSyncAt
    ? moment(entities.lastSyncAt).format('hh:mm A')
    : '';

  useEffect(() => {
    const debounced = _debounce((value: string) => {
      dispatch(updateSearchAndResetPage(value));
    }, 1000);

    debounced(searchText);

    return () => {
      debounced.cancel();
    };
  }, [searchText, dispatch]);

  useEffect(() => {
    if (!ui?.filters) return;

    dispatch(
      fetchSegments({
        sortBy: sortBy,
        sortOrder: sortOrder,
        search: debouncedSearchText || undefined,
        platform: selectedPlatform || undefined,
        rateLevel: rateLevel || undefined,
        page: ui.filters.page || 1,
        recordPerPage: ui.filters.recordPerPage || 25,
      })
    );
  }, [
    dispatch,
    sortBy,
    sortOrder,
    debouncedSearchText,
    selectedPlatform,
    rateLevel,
    ui?.filters?.page,
    ui?.filters?.recordPerPage,
  ]);

  useEffect(() => {
    dispatch(clearSegmentManagerSelection());
  }, [
    dispatch,
    debouncedSearchText,
    selectedPlatform,
    rateLevel,
    sortBy,
    sortOrder,
  ]);

  const refetchSegments = useCallback(() => {
    dispatch(
      fetchSegments({
        sortBy,
        sortOrder,
        search: debouncedSearchText || undefined,
        platform: selectedPlatform || undefined,
        rateLevel: rateLevel || undefined,
        page: ui?.filters?.page || 1,
        recordPerPage: ui?.filters?.recordPerPage || 25,
      })
    );
  }, [
    dispatch,
    sortBy,
    sortOrder,
    debouncedSearchText,
    selectedPlatform,
    rateLevel,
    ui?.filters?.page,
    ui?.filters?.recordPerPage,
  ]);

  const clearSelection = useCallback(
    () => dispatch(clearSegmentManagerSelection()),
    [dispatch]
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setSearchText(event.target.value);
    },
    []
  );

  const handlePlatformChange = useCallback(
    (value: string) => {
      setSelectedPlatform(value);
      dispatch(updateFilters({ page: 1 }));
    },
    [dispatch]
  );

  const handleSortChange = useCallback(
    ({ column }: { column: string }) => {
      if (sortBy === column) {
        setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
      } else {
        setSortBy(column);
        setSortOrder('DESC');
      }
      dispatch(updateFilters({ page: 1 }));
    },
    [sortBy, sortOrder, dispatch]
  );

  const handlePaginationClick = (newPage: number) => {
    dispatch(updateFilters({ page: newPage + 1 }));
  };

  const handleRowsPerPageChange = useCallback(
    (newRowsPerPage: number) => {
      dispatch(updateFilters({ recordPerPage: newRowsPerPage, page: 1 }));
    },
    [dispatch]
  );

  const selectionDisabled = loading || deleteMutationLoading;
  const hasTableRows = (entities?.segments?.length || 0) > 0;

  const pageSegmentIds = useMemo(
    () => (entities?.segments || []).map((s) => s.id).filter(Boolean),
    [entities.segments]
  );

  const selectedOnPageCount = useMemo(
    () => pageSegmentIds.filter((id) => segmentSelectionById[id]).length,
    [pageSegmentIds, segmentSelectionById]
  );

  const allPageRowsSelected =
    pageSegmentIds.length > 0 && selectedOnPageCount === pageSegmentIds.length;

  const headerCheckboxSome = selectedOnPageCount > 0 && !allPageRowsSelected;

  const handleMasterCheckboxChange = useCallback(() => {
    if (selectionDisabled) return;
    if (allPageRowsSelected) {
      dispatch(removeSegmentIdsFromManagerSelection(pageSegmentIds));
    } else {
      const rows = (entities.segments || []).map((s) => ({
        segmentId: s.id,
        segmentName: s.segmentName || s.id,
        rateLevel: s.rateLevel || RateLevel.SYSTEM,
        advertiserId: s.advertiserId,
        partnerId: s.partnerId,
      }));
      dispatch(mergeSegmentsOntoManagerSelection(rows));
    }
  }, [
    allPageRowsSelected,
    dispatch,
    entities.segments,
    pageSegmentIds,
    selectionDisabled,
  ]);

  const selectColumnHeader = useMemo(
    () => (
      <Checkbox
        color="primary"
        indeterminate={headerCheckboxSome}
        checked={allPageRowsSelected}
        disabled={selectionDisabled || !hasTableRows}
        onChange={(e) => {
          e.stopPropagation();
          handleMasterCheckboxChange();
        }}
        onClick={(e) => e.stopPropagation()}
        inputProps={{
          'aria-label': 'Select all segments on this page',
        }}
      />
    ),
    [
      allPageRowsSelected,
      handleMasterCheckboxChange,
      hasTableRows,
      headerCheckboxSome,
      selectionDisabled,
    ]
  );

  const columns = useMemo(
    () =>
      getColumnsByPlatform(
        selectedPlatform,
        sortBy,
        sortOrder,
        selectColumnHeader
      ),
    [selectedPlatform, sortBy, sortOrder, selectColumnHeader]
  );

  const rows = useMemo(() => {
    return entities?.segments?.map((segment) =>
      renderRowCellsByPlatform(selectedPlatform, segment, {
        checked: Boolean(segmentSelectionById[segment.id]),
        disabled: selectionDisabled,
        onToggle: () =>
          dispatch(
            toggleSegmentManagerSelection({
              segmentId: segment.id,
              segmentName: segment.segmentName || segment.id,
              rateLevel: segment.rateLevel || RateLevel.SYSTEM,
              advertiserId: segment.advertiserId,
              partnerId: segment.partnerId,
            })
          ),
      })
    );
  }, [
    dispatch,
    entities.segments,
    selectedPlatform,
    segmentSelectionById,
    selectionDisabled,
  ]);

  const handleConfirmBulkDelete = useCallback(async () => {
    if (selectedSegmentIds.length === 0) return;

    const snapshot = { ...segmentSelectionById };

    setConfirmDeletePending(true);
    try {
      const result = await setTtdOtsBuyableSegments({
        segmentIds: selectedSegmentIds,
        buyable: false,
      });
      const payload = result.data?.setTtdOtsBuyableSegments;
      if (!payload) {
        dispatch(
          addNotification({
            state: LoadingStates.ERROR,
            message: OTS_SGM_MNG_BULK_DELETE_NETWORK_ERROR,
          })
        );
        setConfirmDeleteOpen(false);
        return;
      }

      const topLevelErrors = (payload.errors || []).filter(
        (msg): msg is string => Boolean(msg && String(msg).trim())
      );
      if (topLevelErrors.length > 0) {
        topLevelErrors.forEach((msg) => {
          dispatch(
            addNotification({
              state: LoadingStates.ERROR,
              message: String(msg).trim(),
            })
          );
        });
        setConfirmDeleteOpen(false);
        return;
      }

      if (payload.summary.succeeded > 0) {
        refetchSegments();
      }

      if (payload.summary.failed === 0) {
        setDeleteResultsPayload(payload);
        setDeleteResultsFailedRows([]);
        setResultsModalOpen(true);
        setConfirmDeleteOpen(false);
        clearSelection();
        return;
      }

      const failed = payload.results.filter((r) => r.status === 'ERROR');
      const failedRows = failed.map((r) => {
        const meta = snapshot[r.segmentId];
        const qs = buildSegmentDetailQueryParams(
          (meta?.rateLevel as RateLevel) || RateLevel.SYSTEM,
          meta?.advertiserId,
          meta?.partnerId
        );
        const detailUrl = buildSegmentDetailUrl({
          platform: selectedPlatform.toLowerCase(),
          segmentId: r.segmentId,
          queryString: qs,
        });
        return {
          segmentId: r.segmentId,
          segmentName: meta?.segmentName || r.segmentId,
          detailUrl,
        };
      });

      const succeededIds = payload.results
        .filter((r) => r.status === 'SUCCESS')
        .map((r) => r.segmentId);
      dispatch(removeSegmentIdsFromManagerSelection(succeededIds));
      setDeleteResultsPayload(payload);
      setDeleteResultsFailedRows(failedRows);
      setResultsModalOpen(true);
      setConfirmDeleteOpen(false);
    } catch (e) {
      console.error('Bulk delete failed:', e);
      dispatch(
        addNotification({
          state: LoadingStates.ERROR,
          message: OTS_SGM_MNG_BULK_DELETE_NETWORK_ERROR,
        })
      );
    } finally {
      setConfirmDeletePending(false);
    }
  }, [
    selectedSegmentIds,
    segmentSelectionById,
    setTtdOtsBuyableSegments,
    refetchSegments,
    clearSelection,
    dispatch,
    selectedPlatform,
  ]);

  return (
    <>
      <Box className={classes.root}>
        <Header
          title={SEGMENT_MANAGER}
          style={{ padding: '20px 32px' }}
          action={
            loading ? (
              <Styled.LastSyncedContainer>
                <Styled.LastSyncedLabel>
                  {OTS_SGM_MNG_LAST_SYNCED}
                </Styled.LastSyncedLabel>
                <GhostLoader height="16px" width="100px" />
                <GhostLoader height="16px" width="80px" />
              </Styled.LastSyncedContainer>
            ) : entities.lastSyncAt ? (
              <Styled.LastSyncedContainer>
                <Styled.LastSyncedLabel>
                  {OTS_SGM_MNG_LAST_SYNCED}
                </Styled.LastSyncedLabel>
                <Styled.LastSyncedDate>{formattedDate}</Styled.LastSyncedDate>
                <Styled.LastSyncedTime>{formattedTime}</Styled.LastSyncedTime>
              </Styled.LastSyncedContainer>
            ) : null
          }
        />

        <Box height="100%">
          <Styled.SearchArea>
            <ThemeProvider theme={inputs}>
              <Styled.SearchTextField>
                <TextFieldSearch
                  placeholder={OTS_SGM_MNG_SEARCH_SEGMENT_NAME}
                  value={searchText}
                  onChange={handleSearchChange}
                  disabled={loading}
                  style={{
                    width: '100%',
                  }}
                />
              </Styled.SearchTextField>
            </ThemeProvider>
            <Styled.SelectAutoCompletePlatform
              disablePortal
              id="select-segment-platform"
              options={PLATFORM_OPTIONS}
              popupIcon={<CaretDown />}
              disabled={loading}
              value={
                PLATFORM_OPTIONS.find(
                  (opt) => opt.value === selectedPlatform
                ) || null
              }
              onChange={(_, value: any) => {
                if (value?.value !== undefined) {
                  handlePlatformChange(value.value || '');
                }
              }}
              getOptionLabel={(option: any) => option.label}
              renderInput={(params) => (
                <TextFieldSearch
                  {...params}
                  placeholder={OAUTH_PLATFORMS_FILTER}
                  variant="outlined"
                  disabled={loading}
                />
              )}
              renderOption={(props, option: any) => (
                <Styled.SelectAutoCompleteGroupOptions>
                  <li {...props} key={option.value || option.label}>
                    {option.label}
                  </li>
                </Styled.SelectAutoCompleteGroupOptions>
              )}
            />
            <Styled.SelectAutoCompletePlatformType
              disablePortal
              id="select-rate-level"
              options={RATE_LEVEL_OPTIONS}
              popupIcon={<CaretDown />}
              disabled={loading}
              value={
                RATE_LEVEL_OPTIONS.find((opt) => opt.value === rateLevel) ||
                null
              }
              onChange={(_, value: any) => {
                if (value?.value !== undefined) {
                  setRateLevel(value.value || RateLevel.SYSTEM);
                  dispatch(updateFilters({ page: 1 }));
                }
              }}
              getOptionLabel={(option: any) => option.label}
              renderInput={(params) => (
                <TextFieldSearch
                  {...params}
                  placeholder={SEGMENT_RATE_LEVEL_FILTER}
                  variant="outlined"
                  disabled={loading}
                />
              )}
              renderOption={(props, option: any) => (
                <Styled.SelectAutoCompleteGroupOptions>
                  <li {...props} key={option.value || option.label}>
                    {option.label}
                  </li>
                </Styled.SelectAutoCompleteGroupOptions>
              )}
            />
          </Styled.SearchArea>
          {hasTableRows ? (
            <SegmentSelectionToolbar
              visible={selectedSegmentCount > 0}
              selectedCount={selectedSegmentCount}
              selectionDisabled={selectionDisabled}
              onDeleteClick={() => setConfirmDeleteOpen(true)}
              onClear={clearSelection}
            />
          ) : null}
          <div style={{ padding: '24px 32px', background: '#F0EFF1' }}>
            <DataTable
              loading={loading}
              error={error}
              columns={columns}
              rows={(rows as any) || []}
              customRowsPerPageOptions={[25, 50, 100]}
              totalRows={entities?.totalRecords || 0}
              defaultRowsPerPage={ui?.filters?.recordPerPage || 25}
              page={ui?.filters?.page ? ui.filters.page - 1 : 0}
              onSortChange={handleSortChange}
              onPaginationClick={handlePaginationClick}
              onRowsPerPageChange={handleRowsPerPageChange}
              showRowsPerPageDropdown={true}
              noDataMessage={OTS_SGM_MNG_NO_SEGMENTS_FOUND}
              getRowProps={
                hasTableRows
                  ? (rowIndex: number) => {
                      const seg = entities.segments[rowIndex];
                      if (!seg || !segmentSelectionById[seg.id])
                        return undefined;
                      return {
                        style: { background: 'rgba(103, 58, 183, 0.08)' },
                      };
                    }
                  : undefined
              }
            />
          </div>
        </Box>
      </Box>
      <BulkActionConfirmModal
        open={confirmDeleteOpen}
        loading={deleteMutationLoading || confirmDeletePending}
        title={OTS_SGM_MNG_CONFIRM_BULK_DELETE_TITLE}
        headerSummary={bulkDeleteConfirmHeaderSummary}
        body={bulkDeleteConfirmBody}
        cancelLabel={OTS_SGM_MNG_CANCEL}
        confirmLabel={OTS_SGM_MNG_CONFIRM_DELETE}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleConfirmBulkDelete}
      />
      <BulkDeleteResultsModal
        open={resultsModalOpen}
        payload={deleteResultsPayload}
        failedRows={deleteResultsFailedRows}
        onClose={() => {
          setResultsModalOpen(false);
          setDeleteResultsPayload(null);
          setDeleteResultsFailedRows([]);
        }}
      />
      {notifications.map(({ state, message }, i) => (
        <CustomSnackBar
          index={i}
          trigger={true}
          severity={
            state === LoadingStates.DONE
              ? 'success'
              : state === 'warning'
              ? 'warning'
              : 'error'
          }
          message={message || ''}
          key={i}
          removeNotificationFunction={() => dispatch(removeNotification(i))}
        />
      ))}
    </>
  );
};

export default SegmentManager;
